import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  Circle, 
  ChevronRight, 
  ChevronLeft,
  ArrowRight
} from "lucide-react";

/**
 * Reusable Wizard Component
 * Provides step-by-step guided flow for any process
 */

export const WizardStep = ({ 
  number, 
  title, 
  description, 
  isActive, 
  isCompleted, 
  onClick 
}) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-xl transition-all w-full text-left ${
        isActive 
          ? "bg-blue-50 border-2 border-blue-500" 
          : isCompleted 
          ? "bg-green-50 border border-green-200" 
          : "bg-slate-50 border border-slate-200 opacity-60"
      }`}
      disabled={!isCompleted && !isActive}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
        isCompleted 
          ? "bg-green-500 text-white" 
          : isActive 
          ? "bg-blue-600 text-white" 
          : "bg-slate-200 text-slate-500"
      }`}>
        {isCompleted ? (
          <CheckCircle className="w-5 h-5" />
        ) : (
          <span className="font-bold">{number}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold ${isActive ? "text-blue-900" : isCompleted ? "text-green-900" : "text-slate-600"}`}>
          {title}
        </div>
        <div className="text-sm text-slate-500 truncate">{description}</div>
      </div>
      {isActive && <ChevronRight className="w-5 h-5 text-blue-500" />}
    </button>
  );
};

export const WizardProgress = ({ currentStep, totalSteps, steps }) => {
  return (
    <div className="mb-8">
      {/* Progress Bar */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-600">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="text-sm text-slate-500">
          {Math.round((currentStep / totalSteps) * 100)}% Complete
        </span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
      
      {/* Step Indicators */}
      <div className="flex justify-between mt-4">
        {steps.map((step, index) => (
          <div 
            key={index}
            className="flex flex-col items-center"
            style={{ width: `${100 / steps.length}%` }}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              index + 1 < currentStep 
                ? "bg-green-500 text-white" 
                : index + 1 === currentStep 
                ? "bg-blue-600 text-white" 
                : "bg-slate-200 text-slate-500"
            }`}>
              {index + 1 < currentStep ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                index + 1
              )}
            </div>
            <span className={`text-xs mt-1 text-center ${
              index + 1 <= currentStep ? "text-slate-700 font-medium" : "text-slate-400"
            }`}>
              {step.shortTitle || step.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const WizardContainer = ({ 
  children, 
  currentStep, 
  totalSteps, 
  onNext, 
  onBack, 
  onComplete,
  canProceed = true,
  nextLabel = "Continue",
  backLabel = "Back",
  completeLabel = "Complete"
}) => {
  const isLastStep = currentStep === totalSteps;
  
  return (
    <div className="space-y-6">
      {children}
      
      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-slate-200">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={currentStep === 1}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          {backLabel}
        </Button>
        
        {isLastStep ? (
          <Button
            onClick={onComplete}
            disabled={!canProceed}
            className="bg-green-600 hover:bg-green-700 gap-2"
          >
            {completeLabel}
            <CheckCircle className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            onClick={onNext}
            disabled={!canProceed}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            {nextLabel}
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const WizardSidebar = ({ steps, currentStep, onStepClick }) => {
  return (
    <div className="space-y-2">
      {steps.map((step, index) => (
        <WizardStep
          key={index}
          number={index + 1}
          title={step.title}
          description={step.description}
          isActive={currentStep === index + 1}
          isCompleted={currentStep > index + 1}
          onClick={() => onStepClick(index + 1)}
        />
      ))}
    </div>
  );
};

export const WizardInfoPanel = ({ title, icon: Icon, children, variant = "info" }) => {
  const variants = {
    info: "bg-blue-50 border-blue-200 text-blue-800",
    success: "bg-green-50 border-green-200 text-green-800",
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    tip: "bg-purple-50 border-purple-200 text-purple-800"
  };
  
  return (
    <div className={`p-4 rounded-xl border ${variants[variant]}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />}
        <div>
          {title && <div className="font-semibold mb-1">{title}</div>}
          <div className="text-sm opacity-90">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default WizardContainer;
